import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { API_URLS } from '../../../config/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Tag,
  Palette,
  Hash
} from 'lucide-react';

const AdminBlogCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    sort_order: 0
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(API_URLS.ADMIN.ADMIN_API + '?action=getBlogCategories');
      const result = await response.json();
      if (result.status === 'success') {
        setCategories(result.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingCategory 
        ? API_URLS.ADMIN.ADMIN_API + '?action=updateBlogCategory'
        : API_URLS.ADMIN.ADMIN_API + '?action=createBlogCategory';
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          ...(editingCategory && { id: editingCategory.id })
        })
      });

      const result = await response.json();
      if (result.status === 'success') {
        await fetchCategories();
        resetForm();
        alert(result.message);
      } else {
        alert('Hata: ' + result.message);
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Kategori kaydedilirken hata oluştu');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      color: category.color,
      sort_order: category.sort_order
    });
    setShowForm(true);
  };

  const handleDelete = async (categoryId) => {
    if (!confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URLS.ADMIN.ADMIN_API}?action=deleteBlogCategory&category_id=${categoryId}`,
        { method: 'GET' }
      );
      
      const result = await response.json();
      if (result.status === 'success') {
        await fetchCategories();
        alert(result.message);
      } else {
        alert('Hata: ' + result.message);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Kategori silinirken hata oluştu');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      sort_order: 0
    });
    setEditingCategory(null);
    setShowForm(false);
  };

  const toggleActive = async (category) => {
    try {
      const response = await fetch(API_URLS.ADMIN.ADMIN_API + '?action=updateBlogCategory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: category.id,
          name: category.name,
          description: category.description,
          color: category.color,
          sort_order: category.sort_order,
          is_active: !category.is_active
        })
      });

      const result = await response.json();
      if (result.status === 'success') {
        await fetchCategories();
      } else {
        alert('Hata: ' + result.message);
      }
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Kategori güncellenirken hata oluştu');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 md:p-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 break-words">Blog Kategorileri</h1>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 mt-1 break-words">Blog yazıları için kategori yönetimi</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto flex items-center justify-center gap-2">
          <Plus className="w-4 h-4 flex-shrink-0" />
          <span>Yeni Kategori</span>
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card>
          <CardHeader className="p-3 sm:p-4 md:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
              <Tag className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="break-words">{editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label htmlFor="name" className="text-xs sm:text-sm">Kategori Adı *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Örn: Web Geliştirme"
                    className="text-sm sm:text-base mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="sort_order" className="text-xs sm:text-sm">Sıralama</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    className="text-sm sm:text-base mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="description" className="text-xs sm:text-sm">Açıklama</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Kategori hakkında kısa açıklama..."
                  rows={3}
                  className="text-sm sm:text-base mt-1"
                />
              </div>

              <div>
                <Label htmlFor="color" className="text-xs sm:text-sm">Renk</Label>
                <div className="flex items-center gap-2 sm:gap-2 mt-1">
                  <Input
                    id="color"
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-12 h-10 sm:w-16 sm:h-10 flex-shrink-0"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#3B82F6"
                    className="flex-1 text-sm sm:text-base"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2 pt-2">
                <Button type="submit" className="bg-green-600 hover:bg-green-700 w-full sm:w-auto flex items-center justify-center gap-2">
                  <Save className="w-4 h-4 flex-shrink-0" />
                  <span>{editingCategory ? 'Güncelle' : 'Oluştur'}</span>
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="w-full sm:w-auto flex items-center justify-center gap-2">
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span>İptal</span>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Categories List */}
      <Card>
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <CardTitle className="text-base sm:text-lg md:text-xl">Kategoriler ({categories.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
          {categories.length === 0 ? (
            <div className="text-center py-6 sm:py-8 text-gray-500">
              <Tag className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-300" />
              <p className="text-sm sm:text-base break-words">Henüz kategori eklenmemiş</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {categories.map((category) => (
                <div key={category.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-start sm:items-center gap-2 sm:gap-4 flex-1 min-w-0 w-full sm:w-auto">
                    <div 
                      className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0 mt-1 sm:mt-0"
                      style={{ backgroundColor: category.color }}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        <h3 className="font-medium text-sm sm:text-base text-gray-900 break-words">{category.name}</h3>
                        <Badge 
                          variant={category.is_active ? "default" : "secondary"}
                          className={`text-[10px] sm:text-xs ${category.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
                        >
                          {category.is_active ? 'Aktif' : 'Pasif'}
                        </Badge>
                        {category.sort_order > 0 && (
                          <Badge variant="outline" className="text-[10px] sm:text-xs">
                            <Hash className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1 flex-shrink-0" />
                            {category.sort_order}
                          </Badge>
                        )}
                      </div>
                      {category.description && (
                        <p className="text-xs sm:text-sm text-gray-500 mt-1 break-words">{category.description}</p>
                      )}
                      <p className="text-[10px] sm:text-xs text-gray-400 mt-1 break-all">
                        Slug: {category.slug}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto justify-end sm:justify-start flex-wrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(category)}
                      className={`text-xs sm:text-sm ${category.is_active ? "text-red-600 hover:text-red-700" : "text-green-600 hover:text-green-700"} flex-shrink-0`}
                    >
                      <span className="hidden sm:inline">{category.is_active ? 'Pasif Yap' : 'Aktif Yap'}</span>
                      <span className="sm:hidden">{category.is_active ? 'Pasif' : 'Aktif'}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(category)}
                      className="flex-shrink-0 h-8 sm:h-9 w-8 sm:w-auto px-2 sm:px-3"
                    >
                      <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline ml-1">Düzenle</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(category.id)}
                      className="text-red-600 hover:text-red-700 flex-shrink-0 h-8 sm:h-9 w-8 sm:w-auto px-2 sm:px-3"
                    >
                      <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline ml-1">Sil</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBlogCategories;


